import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, ArrowRight } from "lucide-react";
import { companyInfo as mockCompany, services as mockServicesList, galleryImages } from "../data/mock";
import { useServices } from "../hooks/useContent";
import PageHero from "../components/PageHero";

const serviceImages = {
  "servicii-complete-inmormantare-constanta": { src: galleryImages.churchInterior, alt: "Servicii complete de înmormântare în Constanța – Casa Funerară Sfântul Andrei" },
  "servicii-funerare-lumina": { src: galleryImages.sfAndreiCruce, alt: "Servicii funerare în Comuna Lumina – Casa Funerară Sfântul Andrei" },
  "organizare-inmormantare-completa": { src: galleryImages.waxCandle, alt: "Organizare înmormântare completă de la A la Z în Constanța" },
  "transport-funerar-local": { src: galleryImages.van, alt: "Transport funerar autorizat în Constanța – Casa Funerară Sfântul Andrei" },
  "transport-funerar-national": { src: galleryImages.mercedes, alt: "Transport funerar național din Constanța – vehicule autorizate la standarde europene" },
  "transport-funerar-international": { src: galleryImages.vanBranding, alt: "Transport funerar internațional și repatriere – Casa Funerară Sfântul Andrei" },
  "intocmire-acte-deces-documentatie": { src: galleryImages.sfAndreiBiserica, alt: "Întocmire acte de deces și documentație funerară completă în Constanța" },
  "imbalsamare-toaletare-decedat": { src: galleryImages.celestial, alt: "Pregătire și toaletare decedat cu demnitate – servicii funerare Constanța" },
  "servicii-incinerare": { src: galleryImages.sfAndreiVitraliu, alt: "Servicii de incinerare în Constanța – Casa Funerară Sfântul Andrei" },
  "aranjamente-florale-funerare": { src: galleryImages.flowers1, alt: "Coroane și aranjamente florale funerare în Constanța – Casa Funerară Sfântul Andrei" },
  "parastas-pomeniri": { src: galleryImages.parastasPackages, alt: "Organizare parastas și pomeniri în Constanța – pachete complete" },
  "coliva-pachete-traditionale": { src: galleryImages.coliva, alt: "Colivă tradițională și pachete de pomenire – Casa Funerară Sfântul Andrei" },
  "consiliere-ajutor-inmormantare": { src: galleryImages.sfAndreiGrau, alt: "Consiliere ajutor de înmormântare 2026 – Casa Funerară Sfântul Andrei Constanța" },
  "servicii-funerare-non-stop": { src: galleryImages.opelVanFront, alt: "Servicii funerare NON-STOP 24/7 în Constanța – preluare imediată" },
};

export default function ServicesPage() {
  const { data: services } = useServices();
  return (
    <div>
      <Helmet>
        <title>Servicii Funerare Constanța – Lista Completă | Casa Funerară Sfântul Andrei</title>
        <meta name="description" content="Servicii funerare complete în Constanța: înmormântare, transport funerar local/național/internațional, aranjamente florale, acte deces, incinerare. NON-STOP 24/7." />
        <link rel="canonical" href="https://funeraresfantulandrei.ro/servicii" />
        <meta property="og:title" content="Servicii Funerare Constanța – Casa Funerară Sfântul Andrei" />
        <meta property="og:url" content="https://funeraresfantulandrei.ro/servicii" />
      </Helmet>
      <PageHero
        image={galleryImages.sfAndreiCruce}
        title="Servicii Funerare în Constanța și Lumina"
        subtitle="Oferim o gamă completă de servicii funerare, de la organizarea înmormântării până la transport internațional și consiliere pentru ajutorul de înmormântare. Fiecare serviciu este realizat cu profesionalism și empatie."
        breadcrumbs={[
          { label: "Acasă", to: "/" },
          { label: "Servicii" },
        ]}
      />

      <section className="section-spacing bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 stagger-children">
            {(Array.isArray(services) ? services : []).map((service) => {
              const imgData = serviceImages[service.slug];
              return (
                <Link
                  key={service.slug}
                  to={`/servicii/${service.slug}`}
                  className="group no-underline block rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                  style={{ border: "1px solid var(--warm-border)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
                  data-testid={`service-card-${service.slug}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    {imgData ? (
                      <img
                        src={imgData.src}
                        alt={imgData.alt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full" style={{ backgroundColor: "var(--warm-bg)" }} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3
                      className="text-base font-semibold mb-2 leading-snug"
                      style={{ fontFamily: "'Playfair Display', serif", color: "var(--black)" }}
                    >
                      {service.shortTitle}
                    </h3>
                    <p className="text-sm leading-relaxed line-clamp-2 mb-5" style={{ color: "var(--text-muted)" }}>
                      {service.intro.substring(0, 110)}...
                    </p>
                    <div
                      className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider group-hover:gap-3 transition-all duration-300"
                      style={{ color: "var(--gold)" }}
                    >
                      Detalii <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-5 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Aveți Nevoie de Ajutor Imediat?
          </h2>
          <p className="text-white/55 max-w-xl mx-auto mb-10">
            Suntem disponibili NON-STOP, 24/7. Sunați-ne și vă oferim sprijin complet.
          </p>
          <a
            href={`tel:${mockCompany.phone}`}
            className="inline-flex items-center gap-3 bg-white text-[#0F172A] px-10 py-4 text-sm font-semibold tracking-wide no-underline hover:bg-white/90 transition-colors"
            style={{ borderRadius: "var(--radius-pill)" }}
            data-testid="services-cta-btn"
          >
            <Phone className="w-4 h-4" />
            Sună Acum – NON-STOP
          </a>
        </div>
      </section>
    </div>
  );
}
